import { Inline, Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import React from 'react'

import {
  usePosStateManager,
  useSaleManager,
  useUserManager,
  useUserSwitchingService,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectCurrentSale } from '../../../state/slices/currentSale/currentSaleSlice'
import { isDiscountDisabled } from './discount-functions'
import { DiscountLabel } from './DiscountLabel'
import { NoteLabel } from './NoteLabel'
import { PromoCode } from './Promotion/PromoCode'

export interface AddRowProps {
  displayDiscountRow: boolean
  setDisplaySaleDiscountRow: (value: boolean) => void
  openDiscountPopover: () => void
  showSaleNoteLink: boolean
  onNoteLinkClick: () => void
}

export const AddRow: React.FC<AddRowProps> = ({
  displayDiscountRow,
  openDiscountPopover,
  setDisplaySaleDiscountRow,
  showSaleNoteLink,
  onNoteLinkClick,
}) => {
  const currentSale = useAppSelector(selectCurrentSale)
  const saleManager = useSaleManager()
  const posStateManager = usePosStateManager()
  const userManager = useUserManager()
  const userSwitching = useUserSwitchingService()
  const disabledDiscount = isDiscountDisabled(posStateManager, currentSale)
  const handleDiscountClick = () => {
    if (disabledDiscount) {
      return
    }
    if (userManager.canApplySaleDiscount()) {
      /**
       * The discount link will be removed from the DOM soon after being clicked
       * need setTimeout here so that the Popover.handleDocumentClick method can close any existing opened popover first
       * then the SaleDiscount popover will be opneed
       * Otherwise Popover.handleDocumentClick method exit early and result in two popovers opened at the same time
       */
      setTimeout(() => {
        openDiscountPopover()
        setDisplaySaleDiscountRow(true)
      }, 0)
    } else {
      userSwitching.selectUser({ selectUserForDiscount: true })
    }
  }

  const showPromoCodeLink = !saleManager.hasAppliedPromoCode()

  return (
    <>
      <Text type="signpost">
        <T _str="ADD" _context="Sale footer action row heading" />
      </Text>
      <Inline space={1}>
        {!displayDiscountRow && !currentSale.discount ? (
          <DiscountLabel
            disabled={disabledDiscount}
            onClick={handleDiscountClick}
          />
        ) : null}

        {showPromoCodeLink && <PromoCode />}

        {showSaleNoteLink && <NoteLabel onClick={onNoteLinkClick} />}
      </Inline>
    </>
  )
}
